import { setFileversionIdAndNodeOffsetIdMap } from "../reducers/common";
import { store } from "../ProviderApp";
import { getAllLeafNodesRecur, CubeGlobal } from "@Features/viewer-core";
import _, { isEqual } from "lodash";
import { getIsViewerDependencyLoaded } from "./utils/helpers";
import { getNodeIdAndUniqueIdMapEntry, getLoadedNodeIdAndUniqueIdFileVersionIds } from "./utils/nodeIdAndUniqueIdMapDB";
import { InstanceModifier, ElementType, Matrix, Camera, Color } from "@ts3d-hoops/web-viewer";

export function getFileVersionIdAndOffSetByDesignViewerNodeId(nodeId: any) {
  const state = store.getState();
  const { fileversionIdAndNodeOffsetIdMap } = state;
  let offSet = -1;
  let fileVersionId: any = null;
  for (const key of Object.keys(fileversionIdAndNodeOffsetIdMap)) {
    const nodeOffset = fileversionIdAndNodeOffsetIdMap[key];
    if (nodeOffset !== undefined && nodeOffset <= nodeId && nodeOffset > offSet) {
      fileVersionId = key;
      offSet = nodeOffset;
    }
  }
  return { offSet, fileVersionId };
}

export const getFileversionIdFromAwsfilename = (awsFileName: string): string => {
  const state = store.getState();

  const { filesData } = state;
  const fileVersionId: string | undefined = Object.keys(filesData).find(
    (file) => filesData[file]?.awsFileName == awsFileName,
  );
  if (fileVersionId) return fileVersionId;
  return "";
};

export const getFileVersionIdFromFileId = (fileId: any) => {
  const state = store.getState();
  const { filesData } = state;
  return Object.keys(filesData).find((file) => filesData[file]?.fileId == fileId);
};

export const getFileIdFromFileVersionId = (fileVersionId: any) => {
  const state = store.getState();
  const { filesData } = state;
  return filesData[fileVersionId]?.fileId;
};

export const getCubeUniqueIdFromNodeId = (nodeId: number) => {
  const relativeNodeId = nodeId - CubeGlobal.hwv.model.getNodeIdOffset(nodeId);
  const fileVersionId = getFileversionIdFromAwsfilename(CubeGlobal.hwv.model.getModelFileNameFromNode(nodeId));
  if (fileVersionId) {
    const fileId = getFileIdFromFileVersionId(fileVersionId);
    const fileNodeMaps = getNodeIdAndUniqueIdMapEntry(fileVersionId);
    if (fileNodeMaps) {
      const cubeUniqueId = fileNodeMaps.nodeIdMap?.[relativeNodeId];
      if (cubeUniqueId) {
        return `${fileId}_${cubeUniqueId}`;
      } else {
        return `${fileId}_${relativeNodeId}`;
      }
    } else {
      return `${fileId}_${relativeNodeId}`;
    }
  }
  return false;
};

export const getNodeIdsFromCubeUniqueId = (tempCubeUniqueId: any, mapper?: any, sourceFileVersionId?: any) => {
  if (mapper == undefined) {
    mapper = getFileversionIdAndNodeOffsetIdMap();
  }

  const cubeUniqueId = tempCubeUniqueId.split("_").splice(1).join("_");
  const fileId = tempCubeUniqueId.split("_")[0];

  let nodeIds: any[] = [];

  let isFileFound = false;

  for (const fileVersionId of getLoadedNodeIdAndUniqueIdFileVersionIds()) {
    const tempFileId = getFileIdFromFileVersionId(fileVersionId);
    if (tempFileId == fileId) {
      isFileFound = true;
      nodeIds = getNodeIdAndUniqueIdMapEntry(fileVersionId)?.cubeUniqueIdMap[cubeUniqueId] ?? [];
      if (Array.isArray(nodeIds) && nodeIds.length) {
        const nodeOffset = mapper[fileVersionId];
        nodeIds = nodeIds.map((node: any) => node + nodeOffset);
      } else {
        nodeIds = [];
      }
    }
  }

  if (!isFileFound && sourceFileVersionId) {
    const nodeOffset = mapper[sourceFileVersionId];
    nodeIds.push(Number(cubeUniqueId) + nodeOffset);
  }

  return nodeIds;
};

export const handleModelReset = ({ viewerImpl, isModelStructureReady, isSettingsApplied }: any) => {
  if (getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
    viewerImpl.hwv.model.reset();
    viewerImpl.hwv.model.setInstanceModifier(
      InstanceModifier.DoNotSelect,
      [viewerImpl.hwv.model.getAbsoluteRootNode()],
      false,
    );
    viewerImpl.hwv.model.resetNodesColor([viewerImpl.hwv.model.getAbsoluteRootNode()]);
    viewerImpl.hwv.model.setInstanceModifier(
      InstanceModifier.DoNotXRay,
      [viewerImpl.hwv.model.getAbsoluteRootNode()],
      false,
    );
  }
};

export const getFileversionIdAndNodeOffsetIdMap = (fileVersionId?: string) => {
  const state = store.getState();
  const { fileversionIdAndNodeOffsetIdMap, filesData, getAccessPermission }: any = state;

  let fileVersions: any[] = [];

  if (_.get(filesData, `${fileVersionId}.resourceType`, "") === "RESOURCE_TYPE_MERGED_FILE") {
    // The mapper below is built only from models actually loaded into HOOPS, so the
    // short-circuit must compare against the LOADABLE constituents. Counting deleted,
    // unconverted or permission-restricted ones would make it permanently false and
    // re-walk the whole node tree on every call (this runs from per-node handlers).
    const access = getAccessPermission?.fileVersionId === fileVersionId ? getAccessPermission : undefined;
    fileVersions = _.get(filesData, `${fileVersionId}.sourceFileVersionId`, [])
      .filter(
        (file: { id: string; isDeleted?: unknown; conversionStatus?: string }) =>
          !file.isDeleted && file.conversionStatus === "COMPLETED" && access?.files?.[file.id]?.canViewFile !== false,
      )
      .map((file: { id: any }) => file.id);
  } else {
    fileVersions = [fileVersionId];
  }

  const mappedFileVersionIds = new Set(Object.keys(fileversionIdAndNodeOffsetIdMap));
  if (fileVersions.every((val: string) => mappedFileVersionIds.has(val))) {
    return fileversionIdAndNodeOffsetIdMap;
  }

  const mapper: any = {};

  const absoluteNode = CubeGlobal.hwv.model.getAbsoluteRootNode();
  const modelFileNodes = CubeGlobal.hwv.model.getNodeChildren(absoluteNode);

  for (const node of modelFileNodes) {
    const awsFilename = CubeGlobal.hwv.model.getModelFileNameFromNode(node);
    const fileVersionId = getFileversionIdFromAwsfilename(awsFilename);
    if (fileVersionId && !mapper[fileVersionId]) {
      const nodeOffset = CubeGlobal.hwv.model.getNodeIdOffset(node);
      mapper[fileVersionId] = nodeOffset;
    }
  }
  if (!isEqual(fileversionIdAndNodeOffsetIdMap, mapper)) {
    store.dispatch(setFileversionIdAndNodeOffsetIdMap(mapper));
  }
  return mapper;
};

export const modelTransfroms = {
  toJson: () => {
    const modelVsMatrixMap: any[][] = [];
    const absRootNode = CubeGlobal.hwv.model.getAbsoluteRootNode();
    const modelNodes = CubeGlobal.hwv.model.getNodeChildren(absRootNode);
    for (const modelNode of modelNodes) {
      const modelTransform = CubeGlobal.hwv.model.getNodeMatrix(modelNode);
      const fileVersionId = getFileversionIdFromAwsfilename(CubeGlobal.hwv.model.getModelFileNameFromNode(modelNode));
      modelVsMatrixMap.push([fileVersionId, modelTransform.toJson()]);
    }

    return modelVsMatrixMap;
  },
  fromJson: async (modelVsMatrixMapjson: any[]) => {
    // If the model is a drawing don't reset the transform, transforms works well only for 3d models.
    const sheetIds = CubeGlobal.hwv.sheetManager.getSheetIds();
    if (sheetIds.length > 0) return;

    for (const element of modelVsMatrixMapjson) {
      const fileVersionIdinput = element[0];
      const matrix = Matrix.fromJson(element[1]);
      const absRootNode = CubeGlobal.hwv.model.getAbsoluteRootNode();
      const modelNodes = CubeGlobal.hwv.model.getNodeChildren(absRootNode);

      for (const modelNode of modelNodes) {
        const fileVersionId = getFileversionIdFromAwsfilename(CubeGlobal.hwv.model.getModelFileNameFromNode(modelNode));
        if (fileVersionIdinput == fileVersionId) {
          CubeGlobal.hwv.model.setNodeMatrix(modelNode, matrix);
        }
      }
    }
  },
};

export const cubeCuttingData = {
  toJson: () => CubeGlobal.hwv.cuttingManager.toJson(),
  fromJson: async (cuttingPlaneJson: any) => await CubeGlobal.hwv.cuttingManager.fromJson(cuttingPlaneJson),
};

export const cubeCameraData = {
  toJson: () => CubeGlobal.hwv.view.getCamera().toJson(),
  fromJson: (cameraJson: any) => CubeGlobal.hwv.view.setCamera(Camera.fromJson(cameraJson), 400),
};

export const cubeColorData = {
  toJson: async () => {
    const colors: any[][] = [];
    const colorMap = await CubeGlobal.hwv.model.getNodeColorMap(
      CubeGlobal.hwv.model.getAbsoluteRootNode(),
      ElementType.Faces,
    );
    for (const [a, b] of colorMap) {
      const data = getCubeUniqueIdFromNodeId(a);
      if (data) {
        colors.push([data, b.toJson()]);
      }
    }
    return colors;
  },
  // The color from json is getting parsed and activated at view point itself
  fromJson: (colorsJson: any[]) => {
    const colorMap = new Map();
    for (const val of colorsJson) {
      colorMap.set(val[0], Color.fromJson(val[1]));
    }
    CubeGlobal.hwv.model.setNodesColors(colorMap);
  },
};

export const cubeTransparencyData = {
  toJson: () => {
    const state = store.getState();
    const { transparentElements, GetSettingModalData } = state;
    let transparentCubeUniqueIdElements = transparentElements.map((nodeId: any) => getCubeUniqueIdFromNodeId(nodeId));
    transparentCubeUniqueIdElements = transparentCubeUniqueIdElements.filter(Boolean);

    return {
      elements: transparentCubeUniqueIdElements,
      value: GetSettingModalData.General_transparency,
    };
  },
  fromJson: (data: { elements: any; value: number }) => {
    CubeGlobal.hwv.model.setNodesOpacity(data.elements, 1 - data.value);
  },
};

export const cubeVisibilityData = {
  toJson: async () => {
    const visibilityExceptions: string[] = [];

    const visibilityData = await CubeGlobal.hwv.model.getVisibilityState(CubeGlobal.hwv.model.getAbsoluteRootNode());

    for (const val of visibilityData.visibilityExceptions) {
      const cubeUniqueId = getCubeUniqueIdFromNodeId(val);
      if (cubeUniqueId) {
        visibilityExceptions.push(cubeUniqueId);
      }
    }

    const { defaultVisibility } = visibilityData;
    return {
      visibilityExceptions,
      defaultVisibility,
    };
  },

  fromJson: async (defaultVisibility: any, visibilityExceptions: any) => {
    await CubeGlobal.hwv.model.setNodesVisibility([CubeGlobal.hwv.model.getAbsoluteRootNode()], defaultVisibility);
    await CubeGlobal.hwv.model.setNodesVisibility(visibilityExceptions, !defaultVisibility);
  },
};

export const cubeMagnitudeData = {
  toJson: () => CubeGlobal.hwv.explodeManager.getMagnitude(),
  fromJson: async (magnitude: any) => await CubeGlobal.hwv.explodeManager.setMagnitude(magnitude),
};

export const cubeFaceVisibility = {
  toJson: () => CubeGlobal.hwv.view.getFaceVisibility(),
  fromJson: async (faceVisibility: any) => await CubeGlobal.hwv.view.setFaceVisibility(faceVisibility),
};

export const cubeSnapshot = {
  toJson: async () => {
    const snapshot = await CubeGlobal.hwv.takeSnapshot();
    return snapshot.src;
  },
};

export const cubeLineVisibility = {
  toJson: () => CubeGlobal.hwv.view.getLineVisibility(),
  fromJson: async (lineVisibility: any) => await CubeGlobal.hwv.view.setLineVisibility(lineVisibility),
};

export const cubeSheetId = {
  toJson: () => CubeGlobal.hwv.sheetManager.getActiveSheetId(),
  fromJson: async (sheetId: any) => {
    if (sheetId && sheetId != 0) {
      await CubeGlobal.hwv.sheetManager.setActiveSheetId(sheetId);
    }
  },
};

export const cubeMeasurementsMarkup = {
  toJson: () => CubeGlobal.hwv.measureManager.exportMarkup(),
  fromJson: async (measurements: any) => await CubeGlobal.hwv.measureManager.loadData(measurements),
};

export const cubeMarkup = {
  toJson: () => {
    let markup = [];
    if (CubeGlobal.hwv.markupManager.getActiveMarkupView(CubeGlobal.hwv.view)) {
      const markupJson = CubeGlobal.hwv.markupManager.getActiveMarkupView(CubeGlobal.hwv.view).toJson();
      markup = markupJson.markup;
    }
    return markup;
  },
  fromJson: async (markupJson: { uniqueId?: any; markup?: any }) => {
    // In case issues doesnot have a viewpoint
    const viewerImpl = CubeGlobal.viewerImpl;
    if (Object.keys(markupJson).length) {
      await CubeGlobal.hwv.markupManager.loadMarkupData({ views: [markupJson] });
      await CubeGlobal.hwv.markupManager.activateMarkupViewWithPromise(markupJson.uniqueId, CubeGlobal.hwv.view);
      viewerImpl.loadCustomRedlineMarkups(markupJson.markup);
    }
  },
};

export function handleSelectionLeafNode(selectionsData: string | any[]) {
  const viewerImpl = CubeGlobal.viewerImpl;
  const results: any[] = [];
  for (const selection of selectionsData) {
    const nodeId = selection._nodeId;
    if (CubeGlobal.hwv.model.getNodeChildren(nodeId).length) {
      const leafNodes: any[] = [];
      getAllLeafNodesRecur(nodeId, leafNodes);
      for (const node of leafNodes) {
        const data = getCubeUniqueIdFromNodeId(node);
        const name = CubeGlobal.hwv.model.getNodeName(nodeId);
        if (data) {
          results.push({
            ...selection.toJson(),
            nodeId: data,
            name,
          });
        }
      }
    } else {
      const data = getCubeUniqueIdFromNodeId(nodeId);
      const name = viewerImpl.hwv.model.getNodeName(nodeId);
      if (data) {
        results.push({
          ...selection.toJson(),
          nodeId: data,
          name,
        });
      }
    }
  }
  return results;
}

export const cubeSelectionData = {
  toJson: () => {
    const selectionsData = CubeGlobal.hwv.selectionManager.getResults();
    return handleSelectionLeafNode(selectionsData);
  },
  fromJson: (selectionJson: any) => {
    CubeGlobal.hwv.selectionManager.loadSelectionData(selectionJson);
  },
};

export const cubeCurrentShadingData = {
  toJson: () => CubeGlobal.hwv.view.getDrawMode(),
  fromJson: (mode: any) => CubeGlobal.hwv.view.setDrawMode(mode),
};
